import React, { useEffect, useState, useCallback, useRef } from 'react';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import { t } from '../../lib/i18n';
import { AdminLayout } from '../../components/admin/AdminLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Textarea } from '../../components/ui/textarea';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { toast } from 'sonner';
import {
  Save, Send, Eye, Code, FileText, RotateCcw, Mail,
  CheckCircle, XCircle, ChevronRight, Copy, Tag
} from 'lucide-react';

const CATEGORY_LABELS = {
  auth: { fr: 'Authentification', en: 'Authentication', icon: '🔐' },
  campaign: { fr: 'Campagnes', en: 'Campaigns', icon: '🗡️' },
  billing: { fr: 'Facturation', en: 'Billing', icon: '💳' },
  system: { fr: 'Système', en: 'System', icon: '⚙️' },
  marketing: { fr: 'Marketing', en: 'Marketing', icon: '📢' },
};

const AdminEmailTemplatesPage = () => {
  const { language } = useAuth();
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedKey, setSelectedKey] = useState(null);
  const [template, setTemplate] = useState(null);
  const [saving, setSaving] = useState(false);
  const [editorTab, setEditorTab] = useState('html');
  const [previewHtml, setPreviewHtml] = useState('');
  const [previewSubject, setPreviewSubject] = useState('');
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [testDialogOpen, setTestDialogOpen] = useState(false);
  const [testEmail, setTestEmail] = useState('');
  const [sendingTest, setSendingTest] = useState(false);
  const [seeding, setSeeding] = useState(false);
  const editorRef = useRef(null);

  // Load template list
  const loadTemplates = useCallback(async () => {
    try {
      const res = await axios.get('/api/admin/email-templates');
      setTemplates(res.data.templates || []);
    } catch (err) {
      toast.error('Erreur lors du chargement des templates');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadTemplates(); }, [loadTemplates]);

  // Load single template detail
  const loadTemplate = useCallback(async (key) => {
    try {
      const res = await axios.get(`/api/admin/email-templates/${key}`);
      setTemplate(res.data);
      setSelectedKey(key);
      setEditorTab('html');
      setPreviewHtml('');
    } catch (err) {
      toast.error('Erreur lors du chargement du template');
    }
  }, []);

  // Save template
  const handleSave = async () => {
    if (!template || !selectedKey) return;
    setSaving(true);
    try {
      await axios.put(`/api/admin/email-templates/${selectedKey}`, {
        subject: template.subject,
        html_body: template.html_body,
        text_body: template.text_body,
        enabled: template.enabled,
      });
      toast.success('Template sauvegardé');
      loadTemplates(); // refresh list for enabled status
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  // Preview — saves first then renders server-side preview with sample data
  const handlePreview = async () => {
    if (!selectedKey || !template) return;
    setLoadingPreview(true);
    try {
      // Save first so preview reflects current edits
      await axios.put(`/api/admin/email-templates/${selectedKey}`, {
        subject: template.subject,
        html_body: template.html_body,
        text_body: template.text_body,
        enabled: template.enabled,
      });
    } catch (err) {
      // Save failed but we can still try preview of the saved version
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Erreur de sauvegarde — aperçu de la version enregistrée');
    }
    try {
      const res = await axios.post(`/api/admin/email-templates/${selectedKey}/preview`, {});
      setPreviewHtml(res.data.html);
      setPreviewSubject(res.data.subject);
      setEditorTab('preview');
    } catch (err) {
      toast.error('Erreur lors de la prévisualisation');
    } finally {
      setLoadingPreview(false);
    }
  };

  // Test send
  const handleTestSend = async () => {
    if (!testEmail || !selectedKey) return;
    setSendingTest(true);
    try {
      await axios.post(`/api/admin/email-templates/${selectedKey}/test`, {
        recipient: testEmail,
      });
      toast.success('Email de test envoyé !');
      setTestDialogOpen(false);
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Erreur lors de l\'envoi du test');
    } finally {
      setSendingTest(false);
    }
  };

  // Seed defaults
  const handleSeed = async () => {
    setSeeding(true);
    try {
      await axios.post('/api/admin/email-templates/seed');
      toast.success('Templates par défaut restaurés');
      await loadTemplates();
    } catch (err) {
      toast.error('Erreur lors de la restauration');
    } finally {
      setSeeding(false);
    }
  };

  // Insert variable at cursor
  const insertVariable = (varName) => {
    if (!template) return;
    const insertion = `{{ ${varName} }}`;
    if (editorTab === 'html') {
      setTemplate(prev => ({
        ...prev,
        html_body: (prev.html_body || '') + insertion,
      }));
    } else if (editorTab === 'text') {
      setTemplate(prev => ({
        ...prev,
        text_body: (prev.text_body || '') + insertion,
      }));
    }
    toast.success(`Variable {{ ${varName} }} copiée`, { duration: 1500 });
  };

  // Group templates by category
  const grouped = templates.reduce((acc, tpl) => {
    const cat = tpl.category || 'system';
    if (!acc[cat]) acc[cat] = [];
    acc[cat].push(tpl);
    return acc;
  }, {});

  if (loading) {
    return (
      <AdminLayout>
        <div className="animate-pulse text-muted-foreground">{t('loading', language)}</div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="space-y-4">
        {/* Header */}
        <div className="flex items-center justify-between flex-wrap gap-2">
          <h1 className="text-2xl font-bold font-cinzel flex items-center gap-2">
            <Mail className="h-6 w-6 text-primary" />
            {t('admin_email_templates', language)}
          </h1>
          <div className="flex gap-2">
            <Button variant="outline" onClick={handleSeed} disabled={seeding} size="sm">
              <RotateCcw className="mr-2 h-4 w-4" />
              {seeding ? 'Restauration...' : 'Restaurer les défauts'}
            </Button>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
          {/* Left sidebar: template list */}
          <div className="lg:col-span-3 space-y-3">
            {Object.entries(CATEGORY_LABELS).map(([cat, meta]) => {
              const items = grouped[cat] || [];
              if (items.length === 0) return null;
              return (
                <Card key={cat}>
                  <CardHeader className="py-3 px-4">
                    <CardTitle className="text-sm flex items-center gap-2">
                      <span>{meta.icon}</span>
                      {meta[language] || meta.fr}
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="px-2 pb-2 pt-0">
                    {items.map((tpl) => (
                      <button
                        key={tpl.key}
                        onClick={() => loadTemplate(tpl.key)}
                        className={`w-full text-left px-3 py-2 rounded-md text-sm flex items-center gap-2 transition-colors
                          ${selectedKey === tpl.key
                            ? 'bg-primary/10 text-primary font-medium'
                            : 'hover:bg-muted text-foreground'
                          }`}
                      >
                        {tpl.enabled
                          ? <CheckCircle className="h-3.5 w-3.5 text-green-500 shrink-0" />
                          : <XCircle className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                        }
                        <span className="truncate flex-1">{tpl.name}</span>
                        <ChevronRight className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                      </button>
                    ))}
                  </CardContent>
                </Card>
              );
            })}
          </div>

          {/* Center: editor */}
          <div className="lg:col-span-6">
            {!template ? (
              <Card>
                <CardContent className="py-12 text-center text-muted-foreground">
                  <Mail className="h-12 w-12 mx-auto mb-4 opacity-30" />
                  <p>Sélectionnez un template dans la liste pour l'éditer</p>
                </CardContent>
              </Card>
            ) : (
              <Card>
                <CardHeader className="pb-3">
                  <div className="flex items-center justify-between gap-3">
                    <CardTitle className="text-lg">{template.name}</CardTitle>
                    <div className="flex items-center gap-2">
                      <Switch
                        checked={template.enabled === true}
                        onCheckedChange={(v) => setTemplate(prev => ({ ...prev, enabled: v }))}
                      />
                      <Label className="text-sm">{template.enabled ? 'Activé' : 'Désactivé'}</Label>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="space-y-4">
                  {/* Subject */}
                  <div className="space-y-2">
                    <Label className="text-sm font-medium">Sujet de l'email</Label>
                    <Input
                      value={template.subject || ''}
                      onChange={(e) => setTemplate(prev => ({ ...prev, subject: e.target.value }))}
                      placeholder="Sujet..."
                    />
                  </div>

                  {/* Editor Tabs */}
                  <Tabs value={editorTab} onValueChange={setEditorTab}>
                    <TabsList className="grid w-full grid-cols-3">
                      <TabsTrigger value="html" className="flex items-center gap-1.5">
                        <Code className="h-3.5 w-3.5" /> HTML
                      </TabsTrigger>
                      <TabsTrigger value="text" className="flex items-center gap-1.5">
                        <FileText className="h-3.5 w-3.5" /> Texte
                      </TabsTrigger>
                      <TabsTrigger value="preview" className="flex items-center gap-1.5" onClick={handlePreview}>
                        <Eye className="h-3.5 w-3.5" /> Aperçu
                      </TabsTrigger>
                    </TabsList>

                    <TabsContent value="html" className="mt-3">
                      <Textarea
                        ref={editorRef}
                        value={template.html_body || ''}
                        onChange={(e) => setTemplate(prev => ({ ...prev, html_body: e.target.value }))}
                        className="font-mono text-sm min-h-[400px] bg-[#1e1e2e] text-[#cdd6f4] border-border"
                        placeholder="<html>...</html>"
                        spellCheck={false}
                      />
                    </TabsContent>

                    <TabsContent value="text" className="mt-3">
                      <Textarea
                        value={template.text_body || ''}
                        onChange={(e) => setTemplate(prev => ({ ...prev, text_body: e.target.value }))}
                        className="min-h-[400px]"
                        placeholder="Version texte brut de l'email..."
                      />
                    </TabsContent>

                    <TabsContent value="preview" className="mt-3">
                      {loadingPreview ? (
                        <div className="flex items-center justify-center h-[400px] text-muted-foreground">
                          Chargement de l'aperçu...
                        </div>
                      ) : previewHtml ? (
                        <div className="space-y-2">
                          <div className="text-sm text-muted-foreground">
                            <strong>Sujet :</strong> {previewSubject}
                          </div>
                          <div className="border border-border rounded-md overflow-hidden bg-white">
                            <iframe
                              title="Email Preview"
                              srcDoc={previewHtml}
                              className="w-full h-[400px]"
                              sandbox="allow-same-origin"
                            />
                          </div>
                        </div>
                      ) : (
                        <div className="flex items-center justify-center h-[400px] text-muted-foreground">
                          Cliquez sur l'onglet "Aperçu" pour visualiser le rendu
                        </div>
                      )}
                    </TabsContent>
                  </Tabs>

                  {/* Action buttons */}
                  <div className="flex items-center gap-2 pt-2 border-t">
                    <Button onClick={handleSave} disabled={saving}>
                      <Save className="mr-2 h-4 w-4" />
                      {saving ? 'Sauvegarde...' : 'Sauvegarder'}
                    </Button>
                    <Button variant="outline" onClick={() => setTestDialogOpen(true)}>
                      <Send className="mr-2 h-4 w-4" />
                      Envoyer un test
                    </Button>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>

          {/* Right sidebar: variables */}
          <div className="lg:col-span-3">
            {template && (
              <Card>
                <CardHeader className="py-3 px-4">
                  <CardTitle className="text-sm flex items-center gap-2">
                    <Tag className="h-4 w-4" />
                    Variables disponibles
                  </CardTitle>
                </CardHeader>
                <CardContent className="px-3 pb-3 pt-0">
                  <p className="text-xs text-muted-foreground mb-3">
                    Cliquez pour insérer une variable dans l'éditeur.
                  </p>
                  <div className="space-y-1">
                    {/* Global variables always available */}
                    <div className="text-xs font-medium text-muted-foreground mt-2 mb-1">Globales</div>
                    {['site_name', 'site_url', 'current_year'].map((v) => (
                      <button
                        key={v}
                        onClick={() => insertVariable(v)}
                        className="w-full text-left px-2 py-1.5 rounded text-xs font-mono hover:bg-muted transition-colors flex items-center gap-2"
                      >
                        <Copy className="h-3 w-3 text-muted-foreground" />
                        <span className="text-primary">{'{{ '}{v}{' }}'}</span>
                      </button>
                    ))}

                    {/* Template-specific variables */}
                    {template.variables && template.variables.length > 0 && (
                      <>
                        <div className="text-xs font-medium text-muted-foreground mt-3 mb-1">Spécifiques</div>
                        {template.variables.filter(v => !['site_name', 'site_url', 'current_year'].includes(v)).map((v) => (
                          <button
                            key={v}
                            onClick={() => insertVariable(v)}
                            className="w-full text-left px-2 py-1.5 rounded text-xs font-mono hover:bg-muted transition-colors flex items-center gap-2"
                          >
                            <Copy className="h-3 w-3 text-muted-foreground" />
                            <span className="text-primary">{'{{ '}{v}{' }}'}</span>
                          </button>
                        ))}
                      </>
                    )}
                  </div>

                  {/* Template info */}
                  <div className="mt-4 pt-3 border-t space-y-2">
                    <div className="text-xs text-muted-foreground">
                      <strong>Clé :</strong> <code className="text-xs">{template.key}</code>
                    </div>
                    {template.updated_at && (
                      <div className="text-xs text-muted-foreground">
                        <strong>Modifié :</strong> {new Date(template.updated_at).toLocaleString('fr-FR')}
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </div>

      {/* Test Email Dialog */}
      <Dialog open={testDialogOpen} onOpenChange={setTestDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Envoyer un email de test</DialogTitle>
            <DialogDescription>
              L'email sera envoyé avec des données d'exemple pour les variables.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-2">
              <Label>Adresse email du destinataire</Label>
              <Input
                type="email"
                value={testEmail}
                onChange={(e) => setTestEmail(e.target.value)}
                placeholder="test@example.com"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setTestDialogOpen(false)}>
              Annuler
            </Button>
            <Button onClick={handleTestSend} disabled={sendingTest || !testEmail}>
              <Send className="mr-2 h-4 w-4" />
              {sendingTest ? 'Envoi...' : 'Envoyer'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
};

export default AdminEmailTemplatesPage;
